import { WSActionsMenuItem } from "@wingspanhq/fe-component-library/dist/lib/components/WSActions/WSActions";
import {
  IEngagement,
  IRequirementDefinitionResponse
} from "@wingspanhq/payments/dist/interfaces";
import {
  RequirementDefinitionStatus,
  RequirementType
} from "@wingspanhq/payments/dist/interfaces/requirement";
import { ISharedFileRequestSettingsResponse } from "@wingspanhq/shared-files/dist/lib/interfaces";
import { ISignedDocumentTemplateResponse } from "@wingspanhq/signed-documents/dist/lib/interfaces";
import { WSQueryCache } from "@ws-react-query";

import { useFeatureFlags } from "../../../query/hooks/useFeatureFlags";
import { useUserId } from "../../../query/hooks/helpers";
import { QUERY_SHARED_FILE_SETTINGS } from "../../../query/sharedFile/keys";
import { QUERY_SIGNED_DOCUMENT_TEMPLATE } from "../../../query/signedDocuments/keys";
import { listRequirements } from "../../../services/requirements/listRequirements";
import { helloSignClient, helloSignPromise } from "../../../utils/helloSign";
import { selectorIsSharedFileNotNoneContext } from "../../Requirements/selectors/selectorIsSharedFileNotNoneContext";
import { useEditRequirementDefinitionModal } from "../components/EditRequirementDefinitionModal/useEditRequirementDefinitionModal";
import { useFlowCreateNewVersion } from "../components/FlowCreateNewVersion";
import { useArchiveNotAllowedModal } from "../modals/useArchiveNotAllowedModal";
import { useArchiveRequirementModal } from "../modals/useArchiveRequirementModal";
import { useCreateNewVersionModal } from "../modals/useCreateNewVersionModal";
import { useDeleteNotAllowedModal } from "../modals/useDeleteNotAllowedModal";
import { useDeleteRequirementModal } from "../modals/useDeleteRequirementModal";
import { useRestoreRequirementModal } from "../modals/useRestoreRequirementModal";

type Props = {
  requirementDefinition: IRequirementDefinitionResponse;
  engagements: IEngagement[];
  onDelete?: (requirementDefinition: IRequirementDefinitionResponse) => void;
  onView?: (requirementDefinition: IRequirementDefinitionResponse) => void;
};

export const useRequirementDefinitionDefaultActions = () => {
  const archiveRequirementModal = useArchiveRequirementModal();
  const archiveNotAllowedModal = useArchiveNotAllowedModal();
  const createNewVersionModal = useCreateNewVersionModal();
  const flowCreateNewVersion = useFlowCreateNewVersion();
  const deleteRequirementModal = useDeleteRequirementModal();
  const deleteNotAllowedModal = useDeleteNotAllowedModal();
  const restoreRequirementModal = useRestoreRequirementModal();
  const editRequirementDefinitionModal = useEditRequirementDefinitionModal();

  const userId = useUserId();
  const queryFeatureFlags = useFeatureFlags();

  const getOnPreview = (
    requirementDefinition: IRequirementDefinitionResponse
  ) => {
    const signedDocumentTemplate =
      WSQueryCache.getQueryData<ISignedDocumentTemplateResponse>([
        QUERY_SIGNED_DOCUMENT_TEMPLATE,
        requirementDefinition.dataSourceId
      ]);

    const onPreviewByType: Record<
      RequirementType,
      undefined | (() => Promise<void>)
    > = {
      [RequirementType.Signature]: signedDocumentTemplate?.viewUrl
        ? async () => {
            helloSignClient.open(signedDocumentTemplate.viewUrl!);
            await helloSignPromise(); // refetching to enable re-opening the window. view URLs seems to be one-time
          }
        : undefined,
      [RequirementType.SharedFile]: undefined,
      [RequirementType.BackgroundCheck]: undefined,
      [RequirementType.IdentityVerification]: undefined,
      [RequirementType.InsuranceCoverage]: undefined,
      [RequirementType.Registration]: undefined,
      [RequirementType.TaxVerification]: undefined,
      [RequirementType.PayoutMethod]: undefined
    };

    return onPreviewByType[requirementDefinition.requirementType];
  };

  const getOnEdit = (requirementDefinition: IRequirementDefinitionResponse) => {
    const onEdit = () => {
      editRequirementDefinitionModal.open({
        requirementDefinitionId: requirementDefinition.requirementDefinitionId
      });
    };

    const sharedFileRequestSettings =
      WSQueryCache.getQueryData<ISharedFileRequestSettingsResponse>([
        QUERY_SHARED_FILE_SETTINGS,
        requirementDefinition.dataSourceId
      ]);

    const onEditByType: Record<RequirementType, undefined | (() => void)> = {
      [RequirementType.Signature]: onEdit,
      [RequirementType.SharedFile]:
        sharedFileRequestSettings &&
        !selectorIsSharedFileNotNoneContext(
          sharedFileRequestSettings?.contextType
        )
          ? onEdit
          : undefined,
      [RequirementType.BackgroundCheck]: onEdit,
      [RequirementType.IdentityVerification]: onEdit,
      [RequirementType.InsuranceCoverage]: onEdit,
      [RequirementType.Registration]: onEdit,
      [RequirementType.TaxVerification]: onEdit,
      [RequirementType.PayoutMethod]: onEdit
    };

    return onEditByType[requirementDefinition.requirementType];
  };

  const getMenuActions = async (props: Props): Promise<WSActionsMenuItem[]> => {
    const actions: WSActionsMenuItem[] = [];

    // -- Top section: View, Preview & Edit --
    if (props.onView) {
      actions.push({
        icon: "view",
        label: "View requirement",
        onClick: () => props.onView?.(props.requirementDefinition)
      });
    }

    if (getOnPreview(props.requirementDefinition)) {
      actions.push({
        helpText: "Preview the signature document template",
        icon: "view",
        label: "Preview",
        name: "preview",
        onClick: getOnPreview(props.requirementDefinition)
      });
    }

    if (getOnEdit(props.requirementDefinition)) {
      actions.push({
        helpText: "Update display name or description",
        icon: "edit",
        label: "Edit requirement",
        onClick: getOnEdit(props.requirementDefinition)
      });
    }

    // -- Manage section --

    actions.push({ groupLabel: "MANAGE REQUIREMENT" } as WSActionsMenuItem);

    const allConnectedRequirements = await listRequirements({
      filter: {
        payerId: userId,
        requirementDefinitionId:
          props.requirementDefinition.requirementDefinitionId
      },
      page: {
        size: 100
      }
    });

    const allRequirements = allConnectedRequirements.data;
    const engagementLength = props.engagements.filter(engagement =>
      engagement.requirementDefinitionIds?.includes(
        props.requirementDefinition.requirementDefinitionId
      )
    ).length;

    // -- New version --
    if (queryFeatureFlags.data?.requirementEditing) {
      actions.push({
        helpText: "Change expiration, approval, or requirement settings",
        icon: "customize",
        label: "Create new version",
        onClick: async () => {
          const shouldContinue = await createNewVersionModal.open({
            requirementDefinition: props.requirementDefinition
          });

          if (shouldContinue) {
            flowCreateNewVersion.open({
              requirementDefinitionId:
                props.requirementDefinition.requirementDefinitionId
            });
          }
        }
      });
    }

    if (
      props.requirementDefinition.status === RequirementDefinitionStatus.Active
    ) {
      if (engagementLength > 0) {
        actions.push({
          disabled: true,
          helpText: "Archive the requirement when no longer assigned or in use",
          icon: "archive",
          label: "Archive",
          onDisabledClick: async () => {
            archiveNotAllowedModal.open({
              engagementLength,
              requirementDefinition: props.requirementDefinition
            });
          }
        });
      } else {
        actions.push({
          helpText: "Archive the requirement when no longer assigned or in use",
          icon: "archive",
          label: "Archive",
          onClick: async () => {
            archiveRequirementModal.open({
              engagementLength,
              requirementDefinition: props.requirementDefinition
            });
          }
        });
      }
    } else {
      actions.push({
        icon: "redo",
        label: "Restore",
        onClick: async () => {
          restoreRequirementModal.open({
            engagementLength,
            requirementDefinition: props.requirementDefinition
          });
        }
      });
    }

    // -- Destructive section --

    actions.push({ separator: true } as WSActionsMenuItem);

    if (allRequirements.length > 0) {
      actions.push({
        disabled: true,
        helpText: "Permanently delete if never used by any contractor",
        icon: "trash",
        label: "Delete",
        onDisabledClick: async () => {
          deleteNotAllowedModal.open({
            engagementLength,
            requirementDefinition: props.requirementDefinition
          });
        }
      });
    } else {
      actions.push({
        destructive: true,
        helpText: "Permanently delete if never used by any contractor",
        icon: "trash",
        label: "Delete",
        onClick: async () => {
          deleteRequirementModal.open({
            engagementLength,
            onSuccess: props.onDelete || (() => {}),
            requirementDefinition: props.requirementDefinition
          });
        }
      });
    }

    return actions;
  };

  return { getMenuActions, getOnEdit, getOnPreview };
};
